import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Hanko } from '@teamhanko/hanko-frontend-sdk';

import { UserDetailsStorageService } from '@app/security/data-access/user/user-details-storage.service';

import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class HankoAuthService {
  private readonly router = inject(Router);
  private readonly userDetailsStorage = inject(UserDetailsStorageService);

  readonly hanko = new Hanko(environment.hankoApiUrl, { lang: 'pl' });
  readonly isAuthenticated = signal(false);

  constructor() {
    this.hanko.onSessionCreated(() => this.isAuthenticated.set(true));
    this.hanko.onSessionExpired(() => this.validateAndUpdateSession());
    this.hanko.onUserLoggedOut(() => this.clearSessionState());
    this.hanko.onUserDeleted(() => this.clearSessionState());
  }

  async signOut(): Promise<void> {
    try {
      await this.hanko.logout();
    } catch {
      // Proceed with local signout even if API call fails
    }
    this.clearSessionState();
  }

  async signOutAndRedirect(): Promise<void> {
    await this.signOut();
    await this.router.navigate(['/auth/sign-in']);
  }

  async revalidateSession(): Promise<boolean> {
    return this.validateAndUpdateSession();
  }

  private clearSessionState(): void {
    this.isAuthenticated.set(false);
    this.userDetailsStorage.clearUserDetails();
  }

  private async validateAndUpdateSession(): Promise<boolean> {
    try {
      const session = await this.hanko.validateSession();
      const isValid = session?.is_valid ?? false;
      this.isAuthenticated.set(isValid);
      return isValid;
    } catch {
      this.isAuthenticated.set(false);
      return false;
    }
  }
}
